import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { UilArrowDown } from '@iconscout/react-unicons';
import { StyledGridInner, StyledWrapper } from '../../components/common.style';
import CollapseList from '../../components/CollapseList';
import ArrowDownBtn from '../../components/buttons/ArrowDownBtn';
import { tabletViews } from '../../utils/media';
import { mainDetails } from '../../data/mainDetails';
import { H4Title, Text1 } from '../../components/common.styles.font';

const Wrapper = styled(StyledWrapper)`
    transition: all 0.3s ease-in-out;
`;

const Inner = styled(StyledGridInner)`
    .title {
        grid-area: 1/1/2/2;
    }

    .intro {
        grid-area: 1/2/2/3;
    }

    .title,
    .intro {
        place-self: center;
        margin-bottom: 12rem;
    }

    .challenges-slide {
        grid-area: 2/1/3/3;
    }

    .btn-arrow {
        grid-area: 3/2/4/3;
        justify-self: center;
        align-self: end;
        margin-left: 6rem;
    }

    /* max-width: 1536px */
    @media (max-width: 1536px) {
        .title,
        .intro {
            margin-bottom: 14rem;
        }
    }

    /* max-width: 992px */
    @media ${tabletViews.xlg} {
        .title {
            margin-bottom: 2.6rem;
        }

        .intro {
            margin-bottom: 7rem;
        }

        .btn-arrow {
            margin-left: 12rem;
        }
    }

    /* max-width: 820px */
    @media ${tabletViews.lg} {
        .intro {
            margin-bottom: 5rem;
        }

        .challenges-collapse {
            margin-bottom: 13rem;
        }

        .btn-arrow {
            margin: 0 auto;
        }
    }
`;

const Intro = styled.div`
    width: 100%;
`;

const Slide = styled.div`
    position: relative;
    width: 65rem;
    height: 26rem;

    /* max-width: 1536px */
    @media (max-width: 1536px) {
        height: 28rem;
    }

    /* max-width: 992px */
    @media ${tabletViews.xlg} {
        width: auto;
        margin-bottom: 4rem;
    }

    /* max-width: 820px */
    @media ${tabletViews.lg} {
        display: none;
    }
`;

const SlideIndex = styled.div`
    display: flex;
    align-items: flex-end;
    margin-bottom: 3rem;
    transition: all 0.3s linear;
`;

const TextIndexBtn = styled.button`
    position: relative;
    margin: 0 0.6rem;

    &:not(:last-of-type)::before {
        content: '';
        width: 2px;
        height: 2rem;
        background: #000;
        position: absolute;
        bottom: 0;
        right: -8px;
    }

    &::after {
        content: '';
        width: 100%;
        height: 2px;
        background: #000;
        position: absolute;
        bottom: -1rem;
        left: 0;
        display: none;
    }

    span {
        font-family: 'Roboto Mono', sans-serif;
        text-transform: uppercase;
        font-size: 1.7rem;
        font-weight: 500;
        padding: 0.5rem 0.4rem 0.5rem 0.4rem;
        color: var(--txt-disabled);
    }

    .icon-arrow {
        position: absolute;
        top: -2.5rem;
        left: 50%;
        transform: translateX(-50%);
        display: none;
    }

    &.active::after,
    &.active .icon-arrow {
        display: block;
    }

    &.active span {
        color: var(--txt-active);
    }

    /* max-width: 992px */
    @media ${tabletViews.xlg} {
        span {
            font-size: 1.5rem;
        }
    }

    /* max-width: 820px */
    @media ${tabletViews.lg} {
        span {
            font-size: 1.4rem;
        }
    }
`;

const Cards = styled.div`
    position: relative;
    display: flex;
    margin-bottom: 3rem;
`;

const slider = keyframes`
  0% {
    transform: translateY(30%);
  } 100% {
    transform: translateY(0);
  }
`;

const Card = styled.div`
    width: auto;
    animation: ${slider} 0.5s linear;

    p {
        font-weight: 500;
        margin-bottom: 2rem;
    }
`;

interface Props {
    curLang: string;
}

const ChallengeSection: React.FC<Props> = ({ curLang }) => {
    const { t } = useTranslation();
    const [curActiveSlide, setCurActiveSlide] = useState<number>(1);

    return (
        <Wrapper id="main__challenges" className="bg-tertiary">
            <Inner className="challenges">
                <H4Title
                    className="title title-span-tertiary"
                    data-aos="fade-up"
                    data-aos-delay="80"
                >
                    <span>{t('main.challenges.title')}</span>
                </H4Title>

                <Intro className="intro">
                    <Text1
                        className={`lang-${curLang}`}
                        data-aos="fade-up"
                        data-aos-delay="300"
                        data-aos-duration="1000"
                    >
                        {t('main.challenges.intro')}
                    </Text1>
                </Intro>

                <Slide
                    className="challenges-slide"
                    data-aos="fade-up"
                    data-aos-delay="300"
                    data-aos-duration="1000"
                >
                    <SlideIndex>
                        {mainDetails.challenge.map(({ id, idx, title }) => (
                            <TextIndexBtn
                                key={id}
                                className={
                                    curActiveSlide === idx ? 'active' : ''
                                }
                                onClick={() => setCurActiveSlide(idx)}
                            >
                                <span>{t(title)}</span>
                                <UilArrowDown className="icon-arrow" />
                            </TextIndexBtn>
                        ))}
                    </SlideIndex>

                    <Cards className="challenge-cards">
                        <Card className="challenge-card">
                            <Text1 className={`lang-${curLang}`}>
                                {mainDetails.challenge[curActiveSlide]?.txt &&
                                    t(
                                        mainDetails.challenge[curActiveSlide]
                                            .txt,
                                    )}
                            </Text1>
                        </Card>
                    </Cards>
                </Slide>

                <CollapseList curLang={curLang} />
                <ArrowDownBtn
                    category="btn-arrow"
                    targetElId="solutions-intro"
                    place="center"
                />
            </Inner>
        </Wrapper>
    );
};

export default ChallengeSection;
